import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  delay,
  findGetParameter,
  getData,
  useInterval,
} from "../../components/utilities/UtilityFunctions";
import { Box, Grid, Typography } from "@mui/material";
import { BCPHouse } from "../../components/icons/bcp/BCPIcons";
import {
  CatanCard,
  CatanKnight,
  CatanRoad,
} from "../../components/icons/catan/CatanIcons";

function colorToRGBA(color, opacity) {
  let colors = [0, 0, 0];
  switch (color) {
    case "red":
      colors = [186, 12, 47];
      break;

    case "blue":
      colors = [0, 87, 184];
      break;

    case "orange":
      colors = [255, 200, 69];
      break;

    case "white":
      colors = [242, 241, 240];
      break;

    case "violet":
      colors = [100, 38, 103];
      break;

    case "green":
      colors = [46, 135, 65];
      break;

    default:
      break;
  }

  if (opacity) {
    return `rgba(${colors[0]},${colors[1]},${colors[2]},${opacity})`;
  } else {
    return `rgb(${colors[0]},${colors[1]},${colors[2]})`;
  }
}

export default function StreamOverlayCatan() {
  const { pairingId } = useParams();

  const [player1, setPlayer1] = useState(null);
  const [player2, setPlayer2] = useState(null);
  const [player3, setPlayer3] = useState(null);
  const [player4, setPlayer4] = useState(null);

  const [diceRolls, setDiceRolls] = useState();

  const type = parseInt(findGetParameter("type"));
  const hideRolls = findGetParameter("hideRolls") === "true";

  const interval = 150 * 1000;

  const updatePairing = async (pause) => {
    try {
      if (pause > 0) {
        await delay(pause);
      }

      const apiData = await getData(`/pairings/${pairingId}`, {
        pairingType: "SeatedPairing",
        "expand[]": ["event", "players"],
      });

      for (let i = 1; i <= 4; i++) {
        let _player = {
          name: apiData?.metaData?.[`lbl-player${i}`],
          color: apiData?.metaData?.[`p${i}-color`],
          victoryPoints: apiData?.metaData?.[`p${i}-gamePoints`],
          cards: apiData?.metaData?.[`p${i}-cards`],
          knights: apiData?.metaData?.[`p${i}-knights`],
          longestRoad: apiData?.metaData?.[`p${i}-longestRoad`],
        };
        switch (apiData?.metaData?.[`p${i}-position`]) {
          case "1":
          case 1:
            setPlayer1(_player);
            break;
          case "2":
          case 2:
            setPlayer2(_player);
            break;
          case "3":
          case 3:
            setPlayer3(_player);
            break;
          case "4":
          case 4:
            setPlayer4(_player);
            break;
          default:
            break;
        }
      }

      // Format for dice rolls should be an array of 4 numbers
      setDiceRolls([
        apiData?.metaData?.diceRoll1 === undefined
          ? null
          : apiData?.metaData?.diceRoll1,
        apiData?.metaData?.diceRoll2 === undefined
          ? null
          : apiData?.metaData?.diceRoll2,
        apiData?.metaData?.diceRoll3 === undefined
          ? null
          : apiData?.metaData?.diceRoll3,
        apiData?.metaData?.diceRoll4 === undefined
          ? null
          : apiData?.metaData?.diceRoll4,
      ]);

      updatePairing(10000);
    } catch (err) {}
  };

  useEffect(() => {
    updatePairing();
    // eslint-disable-next-line
  }, []);

  useInterval(() => {
    try {
      updatePairing();
    } catch (err) {}
  }, interval);

  return (
    <Grid
      container
      sx={{
        backgroundColor: "#00ff00", // Lime green bg
        width: "100vw",
        height: "100vh",
        padding: 2,
      }}
    >
      {/* Top Row */}
      <Grid
        item
        xs={6}
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-start",
        }}
      >
        <PlayerCard player={player1} pos={1} />
      </Grid>
      <Grid
        item
        xs={6}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-start",
        }}
      >
        <PlayerCard player={player2} pos={2} />
      </Grid>

      {/* Bottom Row */}
      {type === 3 && <Grid item xs={3}></Grid>}

      <Grid
        item
        xs={type === 1 ? 4 : 3}
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-end",
        }}
      >
        <PlayerCard player={player3} pos={3} />
      </Grid>
      <Grid
        item
        xs={type === 1 ? 4 : 3}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
        }}
      >
        {!hideRolls && <DiceCard rolls={diceRolls} />}
      </Grid>
      <Grid
        item
        xs={type === 1 ? 4 : 3}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <PlayerCard player={player4} pos={4} />
      </Grid>
      {type === 2 && <Grid item xs={3}></Grid>}
    </Grid>
  );

  function PlayerCard(props) {
    const { player, pos } = props;

    if (!player || !pos || !player?.color) return <></>;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        {/* Name and Hex */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <Grid
            container
            sx={{
              width: "325px",
              height: "fit-content",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              opacity: 0.95,
              backgroundColor: "#F0EAD6",
              boxShadow: 4,
            }}
          >
            <Grid
              item
              xs={2}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <BCPHouse sx={{ color: colorToRGBA(player?.color) }} />
            </Grid>

            {/* Player name */}
            <Grid item xs={9}>
              <Typography
                variant="h3"
                sx={{
                  color: "#000",
                  width: "100%",
                  textAlign: "center",
                  padding: "8px",
                }}
              >
                {player?.name?.length > 16
                  ? player?.name.substring(0, 13) + "..."
                  : player?.name || "-----"}
              </Typography>
            </Grid>
          </Grid>

          {/* Endcap with victory points */}
          <Hexagon
            sx={{
              backgroundColor: colorToRGBA(player?.color),
              color: player?.color === "white" ? "#000" : "#fff",
              p: 2,
              boxShadow: 4,
              zIndex: 2,
              ml: "-2px",
            }}
          >
            <Typography variant="h2" component="h4" sx={{ fontSize: "40px" }}>
              {player?.victoryPoints || "0"}
            </Typography>
          </Hexagon>
        </Box>

        {/* Box below card for other points */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
            // position: "absolute",
            p: 1,
            width: "200px",
            mt: "-21px",

            color: player?.color === "white" ? "#000" : "",
            backgroundColor: colorToRGBA(player?.color),

            clipPath:
              "polygon(0 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 12px 100%, 0 calc(100% - 12px))",
          }}
        >
          {/* player items are points. Actual format/structure to tbd.  */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CatanCard color={player?.color === "white" ? "#000" : null} />
            <Typography variant="h3">{player?.cards || "0"}</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CatanKnight color={player?.color === "white" ? "#000" : null} />
            <Typography variant="h3">{player?.knights || "0"}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CatanRoad color={player?.color === "white" ? "#000" : null} />
            <Typography variant="h3">{player?.longestRoad || "0"}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  function DiceCard(props) {
    const { rolls } = props;

    if (!rolls) return <></>;

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: 1,
          alignItems: "center",
          opacity: 0.95,
          height: "108px",
          color: "#000",
          textAlign: "center",
        }}
      >
        {rolls?.length &&
          rolls.map((roll, i) => {
            if (i < 4) {
              return (
                <Box
                  key={i}
                  sx={{
                    pt: 1,
                    pb: 1,
                    backgroundColor: "#F0EAD6",
                    width: "60px",
                    height: "45px",
                  }}
                >
                  <Typography variant="h3">{roll || ""}</Typography>
                </Box>
              );
            } else {
              return <></>;
            }
          })}
      </Box>
    );
  }

  function Hexagon(props) {
    const { sx, children } = props;

    return (
      <Box
        sx={{
          position: "relative",
          width: 70,
          height: 85,
          clipPath:
            "polygon(50% 5%, 100% 23%, 100% 77%, 50% 95%, 0% 77%, 0% 23%)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...sx,
        }}
      >
        {children}
      </Box>
    );
  }
}
